"use client"

import { useMemo } from "react"
import { usePathname } from "next/navigation"
import { useFabCorner, type FabCorner } from "@/hooks/use-fab-corner"
import { useUIStore } from "@/stores/ui-store"

/**
 * Heuristic: pages where the operator is mid-task creating or editing a
 * primary entity. The Submit Feedback FAB shouldn't sit at bottom-right
 * there because every wizard's primary action (Next / Submit / Send) lives
 * in that exact corner — the FAB was eating real clicks (a 2026-05-12
 * Playwright run caught it intercepting the campaign wizard's Next button).
 *
 * Match policy:
 *   - any path segment equal to or ending with "new" → creation page
 *   - any segment containing "create" or "edit" → mutation flow
 * We deliberately use word-boundary segments so we don't false-trigger on
 * "/users/news" or "/newsletters" — the test below catches that.
 */
function isMutationRoute(pathname: string | null): boolean {
  if (!pathname) return false
  const segments = pathname.split("/").filter(Boolean)
  return segments.some(
    (s) =>
      s === "new" ||
      s === "create" ||
      s === "edit" ||
      s.endsWith("-new") ||
      s.endsWith("-create") ||
      s.endsWith("-edit"),
  )
}

/**
 * Effective FAB corner — combines the operator's saved preference with two
 * auto-relocate triggers:
 *
 *   (1) route contains a creation / edit segment ("new", "create", "edit")
 *   (2) at least one modal / dialog / sheet is currently open
 *
 * When either trigger fires we force the FAB to **bottom-left** so the
 * primary action area (bottom-right of the page or modal) stays free.
 * When neither trigger fires we fall back to the operator's saved corner.
 *
 * The auto-override is purely runtime — the saved preference in
 * localStorage is never mutated, so as soon as the wizard closes or the
 * modal dismisses, the FAB animates back to wherever the operator put it.
 */
export function useFabAutoCorner(): {
  effectiveCorner: FabCorner
  savedCorner: FabCorner
  isAutoRelocated: boolean
} {
  const fab = useFabCorner()
  const pathname = usePathname()
  const modalOpenCount = useUIStore((s) => s.modalOpenCount)

  const isAutoRelocated = useMemo(
    () => isMutationRoute(pathname) || modalOpenCount > 0,
    [pathname, modalOpenCount],
  )

  const effectiveCorner: FabCorner = isAutoRelocated ? "bottom-left" : fab.corner

  return {
    effectiveCorner,
    savedCorner: fab.corner,
    isAutoRelocated,
  }
}

/**
 * Translation values for each corner — anchor the FAB at (top:0,left:0) and
 * use `transform: translate(...)` so corner swaps interpolate smoothly via
 * CSS transition. `bottom`/`right` ↔ `auto` toggles cannot transition.
 *
 * Margin = 1.5rem (matches the previous `bottom-6 right-6` Tailwind v4 default).
 * `100% / 100vw` resolve relative to the element's box / viewport at paint
 * time so the translate stays accurate across responsive viewports.
 */
export const FAB_CORNER_TRANSFORM: Record<FabCorner, string> = {
  "bottom-right": "translate(calc(100vw - 100% - 1.5rem), calc(100vh - 100% - 1.5rem))",
  "bottom-left": "translate(1.5rem, calc(100vh - 100% - 1.5rem))",
  "top-right": "translate(calc(100vw - 100% - 1.5rem), 1.5rem)",
  "top-left": "translate(1.5rem, 1.5rem)",
}
